import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  EventAvailable as EventAvailableIcon,
  Refresh as RefreshIcon,
  VpnKey as VpnKeyIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

function PasiIntegrationCard({ t }) {
  const { currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState(null); // plaintext shown once after generate
  const [copiedField, setCopiedField] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const resp = await axios.get(`/api/pasi-integration/?organization=${organizationId}`);
      setData(resp.data);
    } catch (e) {
      console.error("Failed to load PASI integration", e);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const copy = (value, field) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleGenerateKey = async () => {
    setBusy(true);
    try {
      const resp = await axios.post(
        `/api/pasi-integration/generate-key/?organization=${organizationId}`
      );
      setNewKey(resp.data.api_key);
      await fetchStatus();
    } catch (e) {
      console.error("Failed to generate PASI key", e);
    } finally {
      setBusy(false);
    }
  };

  const patchIntegration = async (payload) => {
    setBusy(true);
    try {
      const resp = await axios.patch(
        `/api/pasi-integration/?organization=${organizationId}`,
        payload
      );
      setData(resp.data);
    } catch (e) {
      console.error("Failed to update PASI integration", e);
    } finally {
      setBusy(false);
    }
  };

  const toggleResource = async (resource, exposed) => {
    setBusy(true);
    try {
      await axios.post(`/api/pasi-resources/?organization=${organizationId}`, {
        resource_type: resource.resource_type,
        resource_id: resource.resource_id,
        exposed,
      });
      await fetchStatus();
    } catch (e) {
      console.error("Failed to update PASI resource", e);
    } finally {
      setBusy(false);
    }
  };

  const copyAdornment = (value, field) => ({
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title={copiedField === field ? t("Copied!") : t("Copy to clipboard")}>
          <IconButton size="small" onClick={() => copy(value, field)}>
            {copiedField === field ? <CheckIcon color="success" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </InputAdornment>
    ),
  });

  return (
    <Card variant="outlined" sx={{ gridColumn: { md: "1 / -1" } }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <EventAvailableIcon />
          </Box>
          {data?.enabled && (
            <Chip
              size="small"
              color="success"
              label={t("Active")}
              sx={{ ml: "auto" }}
            />
          )}
        </Box>

        <Typography variant="h6" gutterBottom>
          {t("PASI / Mekonomen booking")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t(
            "Let Mekonomen / PASI bookings flow straight into Verstaani as work orders. Bookings appear in the bookings view and on the calendar."
          )}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            {/* API key */}
            {newKey && (
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  {t("Copy this API key now — it is shown only once.")}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={newKey}
                  type="text"
                  InputProps={{
                    readOnly: true,
                    sx: { fontFamily: "monospace", fontSize: "12px", bgcolor: "background.paper" },
                    ...copyAdornment(newKey, "newkey"),
                  }}
                />
              </Alert>
            )}

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Button
                variant={data?.has_key ? "outlined" : "contained"}
                size="small"
                startIcon={busy ? <CircularProgress size={16} /> : (data?.has_key ? <RefreshIcon /> : <VpnKeyIcon />)}
                onClick={handleGenerateKey}
                disabled={busy}
              >
                {data?.has_key ? t("Regenerate API key") : t("Generate API key")}
              </Button>
              {data?.enabled && (
                <Button
                  variant="text"
                  color="error"
                  size="small"
                  onClick={() => patchIntegration({ enabled: false })}
                  disabled={busy}
                >
                  {t("Disable")}
                </Button>
              )}
              {!data?.enabled && data?.has_key && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => patchIntegration({ enabled: true })}
                  disabled={busy}
                >
                  {t("Enable")}
                </Button>
              )}
            </Box>

            {data?.has_key && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                {t("Current key")}: <code>{data.api_key_prefix}…</code>
              </Typography>
            )}

            {/* Endpoint base URL */}
            <TextField
              fullWidth
              size="small"
              label={t("API endpoint base URL (for PASI)")}
              value={data?.endpoint_base_url || ""}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", fontSize: "12px" },
                ...copyAdornment(data?.endpoint_base_url, "url"),
              }}
              sx={{ mb: 2 }}
            />

            {/* Customer notifications */}
            <FormControlLabel
              control={
                <Switch
                  checked={!!data?.send_customer_notifications}
                  onChange={(e) =>
                    patchIntegration({ send_customer_notifications: e.target.checked })
                  }
                  disabled={busy}
                />
              }
              label={t("Send Verstaani's own confirmation/reminder messages for PASI bookings")}
            />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              {t(
                "Off by default — Mekonomen handles customer communication for PASI bookings (and SMS is billed)."
              )}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* Resources */}
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              {t("Bookable resources")}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
              {t("Expose the resources PASI can book and give Enerius these codes.")}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("Resource")}</TableCell>
                  <TableCell>{t("Bookable in PASI")}</TableCell>
                  <TableCell>{t("Code")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.resources || []).map((r) => (
                  <TableRow key={`${r.resource_type}-${r.resource_id}`}>
                    <TableCell>
                      {r.name}{" "}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={r.resource_type === "employee" ? t("Mechanic") : t("Service bay")}
                        sx={{ ml: 0.5 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={!!r.exposed}
                        onChange={(e) => toggleResource(r, e.target.checked)}
                        disabled={busy}
                      />
                    </TableCell>
                    <TableCell>
                      {r.code ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <code>{r.code}</code>
                          <Tooltip title={copiedField === r.code ? t("Copied!") : t("Copy to clipboard")}>
                            <IconButton size="small" onClick={() => copy(r.code, r.code)}>
                              {copiedField === r.code ? (
                                <CheckIcon color="success" fontSize="small" />
                              ) : (
                                <ContentCopyIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.disabled">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {(data?.resources || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="caption" color="text.secondary">
                        {t("No resources yet. Add employees (and service bays) first.")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>

            <Alert severity="info" sx={{ mt: 2 }}>
              {t(
                "PASI does not send cancellations. If a customer cancels via Mekonomen, edit or delete the booking in Verstaani manually to keep the calendar accurate."
              )}
            </Alert>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default withTranslation()(PasiIntegrationCard);
